import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBatchDefinitions,
  deleteBatchDefinition,
  startBatchRun,
  type BatchDefinitionResponse,
} from '@/api/batchReportsApi';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useToast } from '@/components/ui/toast';
import { Play, Pencil, Trash2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface SavedBatchesListProps {
  className?: string;
  /** When provided, Run/Edit update this instead of navigating (e.g. when already on batch page) */
  onSelectBatch?: (def: BatchDefinitionResponse) => void;
}

export const SavedBatchesList: React.FC<SavedBatchesListProps> = ({ className = '', onSelectBatch }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProject } = useGlobalProject();
  const { showToast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['batchDefinitions'],
    queryFn: async () => {
      const res = await getBatchDefinitions();
      return res.definitions;
    },
  });
  const deleteMutation = useMutation({
    mutationFn: deleteBatchDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchDefinitions'] });
    },
  });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [runningDefId, setRunningDefId] = useState<string | null>(null);

  const definitions = data ?? [];

  const handleRun = async (def: BatchDefinitionResponse) => {
    if (!selectedProject?.id) {
      showToast('error', 'Select a project first');
      if (onSelectBatch) onSelectBatch(def);
      return;
    }
    setRunningDefId(def.id);
    try {
      const run = await startBatchRun({
        project_id: String(selectedProject.id),
        batch_definition_id: def.id,
      });
      showToast('success', 'Batch run started');
      navigate(`/app/reports/batch/${run.id}`);
    } catch (e) {
      showToast('error', (e as Error).message || 'Failed to start batch');
    } finally {
      setRunningDefId(null);
    }
  };

  const handleEdit = (def: BatchDefinitionResponse) => {
    if (onSelectBatch) {
      onSelectBatch(def);
    } else {
      navigate('/app/reports/batch', { state: { batchDefinition: def } });
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    deleteMutation.mutate(id, {
      onSettled: () => {
        setDeleteId(null);
        setConfirmDeleteId(null);
      },
    });
  };

  const openDeleteDialog = (id: string) => setConfirmDeleteId(id);
  const closeDeleteDialog = () => setConfirmDeleteId(null);

  return (
    <div className={className}>
      <h2 className="text-sm font-medium text-foreground mb-1">Saved Batches</h2>
      <p className="text-xs text-muted-foreground mb-3">Re-run or edit a saved batch, or build a new one below.</p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 pb-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : definitions.length === 0 ? (
        <p className="text-sm text-muted-foreground pt-2 pb-4">No saved batches yet.</p>
      ) : (
        <ul className="space-y-2 border border-border rounded-lg divide-y divide-border bg-card">
          {definitions.map((def) => (
            <li
              key={def.id}
              className="flex items-center justify-between gap-4 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium text-foreground">{def.name}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  {def.items?.length ?? 0} template(s)
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={runningDefId === def.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRun(def);
                  }}
                  aria-label="Run batch"
                >
                  {runningDefId === def.id ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-1" />
                  )}
                  Run
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEdit(def);
                  }}
                  aria-label="Edit batch"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={deleteId === def.id}
                  onClick={() => openDeleteDialog(def.id)}
                  aria-label="Delete batch"
                >
                  {deleteId === def.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
                <Dialog open={confirmDeleteId === def.id} onOpenChange={(open) => !open && closeDeleteDialog()}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete batch?</DialogTitle>
                      <DialogDescription>
                        This will remove &quot;{def.name}&quot;. You can create it again later.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" type="button" onClick={closeDeleteDialog}>
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        type="button"
                        onClick={() => handleDelete(def.id)}
                      >
                        Delete
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
